import type { Ref } from 'react'
import { forwardRef } from 'react'

import HTML from '@space-lib/components/html'
import LineClamp from '@space-lib/components/lineClamp'
import { Actions, Entities } from '@space-lib/datahub'
import { StringUtils } from '@space-lib/utils/string'
import drippingNotificationAvatar from '@space-shell/assets/images/drippingNotificationAvatar.svg'

import Avatar from '@space-shell/common/components/avatar'

import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { CLUB_NOTIFICATION_TYPE } from '@space-shell/common/services/notifications'
import { timeSince } from '@space-shell/modules/notifications/utils/date'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const NOTIFICATIONS_CARD_DATA_HUB_ACTIONS = {
  [CLUB_NOTIFICATION_TYPE.COMMENT]: Actions.CLICK_NOTIFICATION_COMMENT,
  [CLUB_NOTIFICATION_TYPE.LIKE]: Actions.CLICK_NOTIFICATION_LIKE,
  [CLUB_NOTIFICATION_TYPE.DRIPPING]: Actions.CLICK_NOTIFICATION_DRIPPING,
  [CLUB_NOTIFICATION_TYPE.REPLY]: Actions.CLICK_NOTIFICATION_REPLY
}

const Unread = styled.div`
  content: '';
  width: 0.5rem;
  height: 0.5rem;
  background-color: var(--red);
  margin-bottom: -0.25rem;
  margin-left: -0.25rem;
`

export interface Props {
  description: string
  timestamp: string
  isRead: boolean
  productName: string
  imageSrc: string
  url: string
  membershipName: string
  type: CLUB_NOTIFICATION_TYPE
  userName: string
}

const NotificationCard = (
  {
    description,
    timestamp,
    isRead,
    url,
    productName,
    imageSrc,
    membershipName,
    type,
    userName
  }: Props,
  ref: Ref<HTMLElement>
) => {
  const { t } = useTranslation()
  const { sendDataHubEvent } = useDataHubEvents()
  const { key: timeLabelKey, value: formattedTime } = timeSince(timestamp)

  const initialLetters = StringUtils.getFirstLettersName(String(userName))
  const urlAvatar = type === CLUB_NOTIFICATION_TYPE.DRIPPING ? drippingNotificationAvatar : imageSrc
  const pluralSufix = formattedTime > 1 ? '_plural' : ''
  const formattedDate = t(`notifications.time_from_now.${timeLabelKey}${pluralSufix}`, {
    value: formattedTime
  })

  const handleClick = () => {
    const eventName = NOTIFICATIONS_CARD_DATA_HUB_ACTIONS[type]

    if (eventName) {
      sendDataHubEvent({
        group: Entities.NOTIFICATIONS,
        name: NOTIFICATIONS_CARD_DATA_HUB_ACTIONS[type]
      })
    }

    window.open(url)
  }

  return (
    <hot-dropdown-menu-item class='_p-4 _pr-5' onClick={handleClick} ref={ref}>
      <div className='_d-flex _cursor-pointer'>
        <div className='_mr-4'>
          {!isRead && <Unread className='_rounded-circle' />}
          <Avatar
            data-test='notification-img'
            className='_bg-blue _text-white'
            size='lg'
            initialLetters={initialLetters}
            urlAvatar={urlAvatar}
          />
        </div>
        <div>
          <LineClamp lines={1} className='_text-1 _font-weight-bold _text-gray-500'>
            {productName}
          </LineClamp>
          <LineClamp lines={3} className='_text-gray-700 _mb-0'>
            <HTML>{description}</HTML>
          </LineClamp>
          <LineClamp lines={1} className='_text-1 _text-gray-500'>
            {`${formattedDate}${membershipName ? ` • ${membershipName}` : ''}`}
          </LineClamp>
        </div>
      </div>
    </hot-dropdown-menu-item>
  )
}

export default forwardRef(NotificationCard)
